import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { useLang } from "../../../i18n/LanguageContext";
import { symbolsClient } from "../api/symbolsClient";

const SUGGESTIONS = {
  pl: ["Księżyc", "Woda", "Klucz", "Wąż", "Dom", "Ptak", "Próg", "Cień", "Droga", "Brama"],
  en: ["Moon", "Water", "Key", "Serpent", "House", "Bird", "Threshold", "Shadow", "Path", "Gate"],
};

const COPY = {
  pl: {
    eyebrow: "Symbolika snów",
    titlePre: "Odczytaj",
    titleAccent: "symbol",
    subtitle: "Wpisz symbol ze snu. Morfeusz odczyta jego znaczenie — prosto albo, po zalogowaniu, z krótkim kontekstem.",
    termsLabel: "Symbole",
    termLabel: "Symbol",
    termPlaceholder: "np. woda, dom, klucz...",
    contextLabel: "Kontekst snu — opcjonalnie",
    contextPlaceholder: "np. uciekałem przez las, a dom był zamknięty",
    contextHint: "Maksymalnie 20 słów. Gość odczytuje prosty symbol bez kontekstu.",
    submit: "Odczytaj symbol",
    loading: "Odczytuję…",
    resultLabel: "Znaczenie",
    suggestionsTitle: "Popularne symbole",
    networkError: "Nie udało się połączyć z symboliką.",
    limitGuest: "Gościnnie: 1 prosty symbol dziennie.",
    limitUser: "Darmowo: 1 symbol dziennie.",
    limitUnlimited: "Symbolika bez limitu.",
    unlockHint: "Po zakupie dowolnej analizy symbolika działa bez limitu.",
    empty: "Podaj symbol.",
    missing: "Brak znaczenia w bibliotece.",
  },
  en: {
    eyebrow: "Dream Symbolism",
    titlePre: "Read a",
    titleAccent: "symbol",
    subtitle: "Enter a dream symbol. Morpheus will read its meaning — simply, or with short context after sign-in.",
    termsLabel: "Symbols",
    termLabel: "Symbol",
    termPlaceholder: "e.g. water, house, key...",
    contextLabel: "Dream context — optional",
    contextPlaceholder: "e.g. I was running through a forest and the house was locked",
    contextHint: "Up to 20 words. Guests can read a simple symbol without context.",
    submit: "Read symbol",
    loading: "Reading…",
    resultLabel: "Meaning",
    suggestionsTitle: "Popular symbols",
    networkError: "Could not connect to the symbolism service.",
    limitGuest: "Guest: 1 simple symbol a day.",
    limitUser: "Free: 1 symbol a day.",
    limitUnlimited: "Unlimited symbolism.",
    unlockHint: "Buy any analysis to unlock unlimited symbols.",
    empty: "Enter a symbol.",
    missing: "No meaning in the library yet.",
  },
};

const splitTerms = (value) => String(value || "").split(",").map((item) => item.trim()).filter(Boolean);

const wordCount = (value) => (String(value || "").trim().match(/[\p{L}\p{N}-]+/gu) || []).length;

const clampContext = (value) => {
  const words = String(value || "").trim().match(/[\p{L}\p{N}-]+/gu) || [];
  if (words.length <= 20) return String(value || "");
  return words.slice(0, 20).join(" ");
};

const limitText = (limit, copy) => {
  if (limit?.unlimited) return copy.limitUnlimited;
  const left = Number.isFinite(limit?.left) ? limit.left : null;
  const total = Number.isFinite(limit?.limit) ? limit.limit : null;
  if (left === null || total === null) return "";
  return `${left} / ${total}`;
};

export const SymbolFinderPage = () => {
  const { lang } = useLang();
  const { isAuthenticated } = useAuth();
  const copy = COPY[lang] || COPY.pl;
  const suggestions = SUGGESTIONS[lang] || SUGGESTIONS.pl;

  const [terms, setTerms] = useState("");
  const [context, setContext] = useState("");
  const [result, setResult] = useState(null);
  const [limit, setLimit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const termsList = useMemo(() => splitTerms(terms), [terms]);
  const canUseContext = isAuthenticated;
  const contextWords = wordCount(context);

  useEffect(() => {
    setResult(null);
    setError("");
    symbolsClient.status().then((data) => setLimit(data.limit || null)).catch(() => {});
  }, [lang, isAuthenticated]);

  const lookup = async (rawTerms = terms) => {
    const nextTerms = splitTerms(rawTerms);
    if (!nextTerms.length) {
      setError(copy.empty);
      return;
    }
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const response = await symbolsClient.interpret({
        terms: nextTerms,
        context: canUseContext ? clampContext(context) : "",
        language: lang,
      });
      setLimit(response.limit || null);
      setResult(response);
    } catch (e) {
      setLimit(e?.response?.limit || limit);
      setError(e?.message || copy.networkError);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    lookup();
  };

  const handleSuggestion = (suggestion) => {
    setTerms(suggestion);
    lookup(suggestion);
  };

  const handleContextChange = (event) => setContext(clampContext(event.target.value));

  const introLimit = isAuthenticated ? copy.limitUser : copy.limitGuest;
  const visibleLimit = limitText(limit, copy);

  return (
    <section className="mdPage" data-testid="symbol-finder-page" data-ui="symbolika-page">
      <div className="mdPage__inner mdPage__inner--wide">
        <div>
          <p className="mdPage__eyebrow">{copy.eyebrow}</p>
          <h1 className="mdPage__title">
            {copy.titlePre} <em>{copy.titleAccent}</em>
          </h1>
          <p className="mdPage__subtitle">{copy.subtitle}</p>
        </div>

        <form className="mdCard" onSubmit={handleSubmit} noValidate data-ui="symbolika-form">
          <div className="mdField">
            <label className="mdField__label" htmlFor="sym-term">{limit?.unlimited ? copy.termsLabel : copy.termLabel}</label>
            <input
              id="sym-term"
              type="text"
              className="mdField__input"
              value={terms}
              onChange={(event) => setTerms(event.target.value)}
              placeholder={copy.termPlaceholder}
              data-testid="symbol-finder-input"
              autoFocus
            />
            <p className="mdField__hint">{introLimit}{visibleLimit ? ` Pozostało: ${visibleLimit}` : ""}</p>
          </div>

          {canUseContext && (
            <div className="mdField">
              <label className="mdField__label" htmlFor="sym-context">{copy.contextLabel}</label>
              <textarea
                id="sym-context"
                className="mdField__input"
                value={context}
                onChange={handleContextChange}
                placeholder={copy.contextPlaceholder}
                rows={3}
                data-ui="symbolika-context"
              />
              <p className="mdField__hint">{copy.contextHint} {contextWords} / 20</p>
            </div>
          )}

          <button
            type="submit"
            className="mdBtn mdBtn--primary"
            disabled={loading || !termsList.length}
            data-testid="symbol-finder-submit"
          >
            {loading ? copy.loading : copy.submit}
          </button>

          {!limit?.unlimited && <p className="mdField__hint">{copy.unlockHint}</p>}

          {error && (
            <div className="mdAlert" data-testid="symbol-finder-error" data-ui="symbolika-error">
              {error}
            </div>
          )}

          {result?.results?.length > 0 && (
            <div className="mdFinder__result" data-testid="symbol-finder-result" data-ui="symbolika-result">
              <p className="mdFinder__resultLabel">{copy.resultLabel}</p>
              {result.results.map((item) => (
                <article key={item.term} data-ui="symbolika-result-item">
                  <h2 className="mdFinder__resultName" data-testid="symbol-finder-result-name">{item.term}</h2>
                  <p className="mdFinder__resultMeaning" data-testid="symbol-finder-result-meaning">
                    {item.meaning || copy.missing}
                  </p>
                </article>
              ))}
            </div>
          )}

          <div className="mdFinder__suggestions" data-testid="symbol-finder-suggestions">
            <p className="mdField__label" data-ui="symbolika-suggestions-title">
              {copy.suggestionsTitle}
            </p>
            {suggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                className="mdChip"
                onClick={() => handleSuggestion(suggestion)}
                data-testid={`symbol-finder-chip-${suggestion}`}
              >
                {suggestion}
              </button>
            ))}
          </div>
        </form>
      </div>
    </section>
  );
};
